/**
 * Order note shared by the cart page and the cart drawer.
 *
 * Both places render the same `cart.note`, so the cart itself stays the single
 * source of truth. This element only has to keep the rendered fields in step
 * with each other while the shopper types, and push the value back to the cart
 * once typing settles.
 */
class CartNote extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('textarea');

    if (!this.input) {
      return;
    }

    if (CartNote.value === undefined) {
      // First field on the page: whatever Liquid rendered is what the cart holds.
      CartNote.value = this.input.value;
      CartNote.saved = this.input.value;
    } else if (this.input.value !== CartNote.value) {
      // Cart sections re-render from a snapshot that can predate the last
      // keystroke, so the shared value wins over the markup.
      this.input.value = CartNote.value;
    }

    this.input.addEventListener('input', this.onInput.bind(this));
    this.input.addEventListener('change', this.onChange.bind(this));
  }

  onInput() {
    CartNote.share(this.input);
    CartNote.debouncedSave();
  }

  onChange() {
    CartNote.share(this.input);
    CartNote.save();
  }

  static share(source) {
    CartNote.value = source.value;

    document.querySelectorAll('cart-note textarea').forEach(field => {
      if (field !== source && field.value !== CartNote.value) {
        field.value = CartNote.value;
      }
    });
  }

  static save() {
    if (CartNote.value === CartNote.saved || !window.routes?.cart_update_url) {
      return;
    }

    const note = CartNote.value;

    CartNote.saved = note;

    // Chained so two quick edits can never be applied out of order, and sent
    // with `keepalive` so a save triggered by leaving the field survives the
    // navigation that triggered it. No `sections` are requested: the note does
    // not change prices or line items, so there is nothing to re-render.
    CartNote.request = CartNote.request
      .then(() =>
        fetch(routes.cart_update_url, {
          ...fetchConfig(),
          keepalive: true,
          body: JSON.stringify({ note })
        })
      )
      .then(response => {
        if (!response.ok) {
          throw new Error(response.status);
        }
      })
      .catch(error => {
        if (CartNote.saved === note) {
          CartNote.saved = undefined;
        }

        console.error('Error saving cart note:', error);
      });
  }
}

CartNote.value = undefined;
CartNote.saved = undefined;
CartNote.request = Promise.resolve();
CartNote.debouncedSave = debounce(() => CartNote.save(), 500);

window.addEventListener('pagehide', () => CartNote.save());

customElements.define('cart-note', CartNote);
